import { useNavigate, Link } from 'react-router-dom';
import { requestChangeHref } from '../utils/request-change';
import { FileText, AlertCircle } from 'lucide-react';
import { MetricCard, METRIC_READ_CAPTIONS, Card, Button, Badge, BodyText, Heading, SectionLabel, Skeleton, Alert } from '@aaimplatform/ui';
import { useQuery } from '@tanstack/react-query';
import { useDashboard } from '../hooks/useDashboard';
import { useInstitutionFlags } from '../hooks/useInstitutionFlags';
import type { InstitutionFlags } from '../hooks/useInstitutionFlags';
import { loanSyndicationClient } from '../api/client';
import { readErrorCopy } from '../api/error-message';
import type { components as LspComponents } from '@aaimplatform/institutional-types/loan-syndication-protocol';

// ---------------------------------------------------------------------------
// Loan Participations Performance tile
// ---------------------------------------------------------------------------
//
// Sprint C14 Lane B WP B-7: surfaces a rollup of the FI's participation
// positions on the Loan Participations network. The tile is conditional
// on `enableLoanParticipations` -- tenants without the flag never see it.
// The component fetches /api/v1/syndication/positions and aggregates
// committed amount, interest earned, and weighted yield client-side.
//
// GH #2742. The read used to swallow its error and return `{ positions: [] }`,
// which aggregated to 0 / $0.0 / $0.0 / 0.00% -- four measured-looking figures
// invented by an error branch, on a money surface. The read now propagates,
// and a failed read renders `--` in every tile, the active COUNT included: a
// count is as much a measurement as a dollar figure.
//
// A 200 carrying zero positions is a DIFFERENT state and keeps rendering real
// zeros -- that FI genuinely holds no participations. Collapsing the two is
// the defect.
//
// A failed read stays non-fatal at PAGE level. The throw is confined to this
// query (retry:false is local), admin-portal registers no ErrorBoundary and no
// throwOnError, so the rest of the dashboard renders; the tile carries its own
// notice.
//
// Measured in prod 2026-08-28, across the 7 tenants with the flag on: OICU
// (..01c0) and Notre Dame FCU (..d0fc) return 403 AMBIGUOUS_PARTICIPANT_CONTEXT
// on this endpoint -- each carries two ACTIVE CrtParticipant rows, the
// condition loan-syndication-protocol/src/middleware/auth.ts strict-guards as
// a data-integrity fault (tracked separately). NO_PARTICIPANT_FOR_TENANT is
// returned to no flag-on tenant today. The other 5 return 200. All 7 rendered
// the four zeros.

const CURRENCY_COMPACT_DASH = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 1,
});
const PERCENT_DASH = new Intl.NumberFormat('en-US', {
  style: 'percent',
  minimumFractionDigits: 2,
});

function formatCurrencyCompact(value: number | null | undefined): string {
  if (value === null || value === undefined) return '--';
  return CURRENCY_COMPACT_DASH.format(value);
}
function formatPercentDash(value: number | null | undefined): string {
  if (value === null || value === undefined) return '--';
  return PERCENT_DASH.format(value);
}

type ParticipationPosition = LspComponents['schemas']['ParticipationPosition'];

/**
 * The `{ error, message }` envelope loan-syndication-protocol returns on a
 * non-2xx (services/loan-syndication-protocol/src/middleware/auth.ts,
 * resolveParticipantContext). The canonical spec declares ONLY a 200 for
 * listParticipationPositions, so openapi-typescript emits no error schema and
 * openapi-fetch types `error` as `never`. Read the envelope defensively
 * through `unknown` rather than asserting a shape the generated types do not
 * carry -- that also makes the extraction cast-free and typecheck-proof.
 */
function readErrorEnvelope(error: unknown): { code?: string; message?: string } {
  if (typeof error !== 'object' || error === null) return {};
  const e = error as { error?: unknown; message?: unknown };
  return {
    code: typeof e.error === 'string' ? e.error : undefined,
    message: typeof e.message === 'string' ? e.message : undefined,
  };
}

// No endpoint returns this shape: it is aggregated client-side FROM the
// generated ParticipationPosition, and yieldReportingCount / positionCount
// exist only to caption the tile. Declaring it in the canonical spec would
// publish a contract no service implements.
// adhoc-type-skip: local derived view-model computed from a generated type, not HTTP surface
export interface PositionRollup {
  activeCount: number;
  totalCommitted: number;
  totalInterest: number;
  /** null when NO position in the book reports an annualizedYield. */
  weightedYield: number | null;
  /** Positions that carried an annualizedYield -- the weighted-yield basis. */
  yieldReportingCount: number;
  /** Positions read -- the coverage denominator for the caption. */
  positionCount: number;
  /**
   * pagination.totalItems: how many ACTIVE positions the server holds, which is
   * not the same as how many this page read. null when the server sent no
   * pagination block.
   */
  totalItems: number | null;
  /**
   * True when the server holds more positions than this page read. The money
   * figures are then a PAGE-1 SUBTOTAL, not the book -- see the tile.
   */
  coveragePartial: boolean;
}

/**
 * Aggregate a SUCCESSFULLY READ position list. Never called for a failed read:
 * the caller renders `--` for that state, and collapsing the two states is the
 * GH #2742 defect.
 *
 * committedAmount and totalInterestReceived are REQUIRED by the contract
 * (schema-registry/loan-syndication-protocol.openapi.yaml:3877-3886), so they
 * are summed directly -- the prior `?? 0` guards were dead on a
 * spec-conformant payload.
 *
 * `totalItems` is the server's ACTIVE count (pagination.totalItems). The tile
 * requests pageSize 100 and summed whatever came back, so an FI with 150 active
 * positions saw the first 100 presented as its whole book -- an understated
 * money figure that looks exactly like a complete one. The sums here still
 * cover only the positions passed in; `coveragePartial` is what tells the tile
 * not to present them as the book.
 *
 * weightedYield is the commitment-weighted mean over the positions that
 * actually REPORT a yield. annualizedYield is OPTIONAL in the contract and the
 * service omits the field entirely when the column is NULL
 * (mappers/position.mapper.ts:54), so counting a non-reporting position as 0%
 * publishes an invented rate. Measured in prod 2026-08-28: both ACTIVE
 * positions carry annualized_yield NULL, and the one visible book ($4.8M, one
 * position) therefore rendered "0.00%" on a SUCCESSFUL read. With no basis the
 * figure is null and the tile renders `--`.
 */
export function aggregatePositionRollup(
  positions: ParticipationPosition[],
  totalItems?: number | null,
): PositionRollup {
  let totalCommitted = 0;
  let totalInterest = 0;
  let yieldBasis = 0;
  let yieldWeighted = 0;
  let yieldReportingCount = 0;

  for (const p of positions) {
    totalCommitted += p.committedAmount;
    totalInterest += p.totalInterestReceived;
    if (typeof p.annualizedYield === 'number') {
      yieldBasis += p.committedAmount;
      yieldWeighted += p.committedAmount * p.annualizedYield;
      yieldReportingCount += 1;
    }
  }

  const resolvedTotal = typeof totalItems === 'number' ? totalItems : null;

  return {
    activeCount: positions.length,
    totalCommitted,
    totalInterest,
    weightedYield: yieldBasis > 0 ? yieldWeighted / yieldBasis : null,
    yieldReportingCount,
    positionCount: positions.length,
    totalItems: resolvedTotal,
    coveragePartial: resolvedTotal !== null && resolvedTotal > positions.length,
  };
}

const READ_FAILED_HEADLINE = 'Participation positions could not be read';
const NOT_ONBOARDED_HEADLINE = 'Not yet on the Loan Participations network';

/**
 * FI-facing wording for the participant-context 403s
 * loan-syndication-protocol returns (src/middleware/auth.ts,
 * resolveParticipantContext). Their `message` is written for Aaim engineers --
 * "Caller's tenant has multiple active CRT participants; data-integrity
 * issue." -- and printed verbatim it told an FI admin about internal records by
 * internal names. The code is stable and the wording is ours; a code this map
 * does not carry still prints the server's message, which is the only account
 * of an unforeseen failure.
 */
export const PARTICIPANT_CONTEXT_COPY: Readonly<Record<string, string>> = {
  AMBIGUOUS_PARTICIPANT_CONTEXT:
    "Your institution has more than one active participation record on the Loan Participations network, so its positions cannot be attributed. Aaim support needs to correct the record.",
  NO_PARTICIPANT_FOR_TENANT:
    'Your institution has no active participation record on the Loan Participations network yet, so there are no positions to read. Network onboarding is not complete.',
};
const ENTITLEMENTS_READ_FAILED_HEADLINE = 'Entitlements could not be read';
// The withheld-read captions are the kit's METRIC_READ_CAPTIONS (GH #2917
// deliverable 5); the local names keep this page's call sites readable.
const READ_FAILED_CAPTION = METRIC_READ_CAPTIONS.failed;
/**
 * The read has not run. Distinct from READ_FAILED_CAPTION, which states that a
 * read ran and rejected, and distinct from any caption describing a book:
 * there is no book here, only an absent measurement.
 */
const NOT_READ_CAPTION = METRIC_READ_CAPTIONS.unread;

export function LiquidityNetworkPerformanceTile() {
  const navigate = useNavigate();
  const query = useQuery({
    queryKey: ['lsp-positions', 'dashboard-rollup'],
    queryFn: async () => {
      const result = await loanSyndicationClient.GET(
        '/api/v1/syndication/positions',
        {
          params: {
            query: { status: 'ACTIVE', page: 1, pageSize: 100 },
          },
        },
      );
      // Read the status BEFORE destructuring. `listParticipationPositions`
      // declares only a 200, so openapi-fetch resolves ErrorResponse to
      // `never`; a const-destructure then narrows the whole binding -- data,
      // error AND response -- to `never` inside `if (error)`, and every
      // property access there is TS2339. Hoisting is what keeps this
      // compiling while still carrying the status out.
      const httpStatus = result.response.status;
      const ok = result.response.ok;
      const { data, error } = result;
      if (error) {
        const { code, message } = readErrorEnvelope(error);
        // `status` and `code` ride on the Error so a future reader can branch
        // without re-parsing the body. Neither has a reader today: this query
        // sets retry:false locally, so main.tsx's isAuthStatus retry predicate
        // is never consulted for it.
        throw Object.assign(new Error(message ?? READ_FAILED_HEADLINE), {
          status: httpStatus,
          code,
        });
      }
      // A 5xx with an EMPTY body populates neither `data` nor `error`:
      // openapi-fetch has nothing to parse, so the queryFn used to return
      // undefined and TanStack rejected it with its own internal string,
      // "Query data cannot be undefined", which surfaced verbatim to the FI
      // operator as the failure detail. Gate on the response instead, so the
      // operator reads an HTTP status they can act on.
      if (!ok || !data) {
        throw Object.assign(
          new Error(
            `HTTP ${httpStatus} from the participations service, with no response body.`,
          ),
          { status: httpStatus, code: undefined },
        );
      }
      // A 200 whose body carries no `positions` array is NOT an empty book.
      // `data?.positions ?? []` aggregated that to 0 / $0.0 / $0.0, which is
      // the GH #2742 shape re-entering through the success branch this time.
      if (!Array.isArray(data.positions)) {
        throw Object.assign(
          new Error(
            `HTTP ${httpStatus} from the participations service, but the response carried no positions list.`,
          ),
          { status: httpStatus, code: undefined },
        );
      }
      return data;
    },
    staleTime: 60 * 1000,
    retry: false,
  });

  // null means NOT READ -- never 0. FOUR distinct states, not three:
  //   isLoading                -> skeletons
  //   isError                  -> rollup null, every tile '--' + READ_FAILED_CAPTION
  //   isPending && !isFetching -> rollup null, every tile '--' + NOT_READ_CAPTION
  //   isSuccess                -> rollup present, real figures INCLUDING real zeros
  //
  // The third state is the one that reads as three states at once if nothing
  // names it. TanStack defines `isLoading` as `isPending && isFetching`
  // (@tanstack/query-core 5.90.12, build/modern/queryObserver.js:310), so a
  // FIRST fetch that is PAUSED -- the browser went offline before this
  // component mounted, networkMode default -- is pending with isFetching
  // false: past the skeleton branch, and neither error nor success. Every
  // VALUE was already withheld there, but the captions fell through to their
  // success wording and asserted 'Across all positions', 'Cumulative' and
  // 'No active participations' over four dashes. The last of those is the
  // exact string a withheld read is forbidden to print, and it claimed an
  // empty book for an FI whose book was never read.
  //
  // So every caption below answers `!rollup` explicitly, before any branch
  // that describes a book. A caption may describe a book only when a rollup
  // exists, which means only after a successful read.
  //
  // The queryFn has already rejected a missing/!ok body and a non-array
  // `positions`, so on isSuccess both are known good -- no `?? []` fallback
  // remains to turn a failed read into a measured zero.
  const rollup = query.isSuccess
    ? aggregatePositionRollup(
        query.data.positions,
        query.data.pagination?.totalItems ?? null,
      )
    : null;
  const readFailed = query.isError;
  // The queryFn rides `code` on the Error it throws; read it through unknown.
  const errorCode: unknown = readFailed
    ? (query.error as unknown as { code?: unknown } | null)?.code
    : undefined;
  const failureCode = typeof errorCode === 'string' ? errorCode : undefined;
  const failureDetail = readFailed
    ? failureCode && Object.prototype.hasOwnProperty.call(PARTICIPANT_CONTEXT_COPY, failureCode)
      ? PARTICIPANT_CONTEXT_COPY[failureCode]
      : query.error instanceof Error
        ? query.error.message
        : READ_FAILED_HEADLINE
    : null;
  const notOnboarded = readFailed && failureCode === 'NO_PARTICIPANT_FOR_TENANT';

  // Active Positions is the ONE tile a partial page can still answer honestly:
  // pagination.totalItems is the server's own ACTIVE count, independent of how
  // many rows this page carried.
  const activeValue = (() => {
    if (!rollup) return '--';
    return (rollup.totalItems ?? rollup.activeCount).toString();
  })();

  // GH #2917 deliverable 5. Every caption below used to return a withheld
  // wording OR a measured statement through one expression, and each of them rode
  // MetricCard's `change` slot, which sets monospace and takes the changeType
  // color. A withheld read was drawn with a measured movement's affordance. The
  // two are separate now: this one rides the `caption` slot, and the notes
  // below stay on `change`, reachable only once a rollup exists.
  const withheldCaption: string | undefined = readFailed
    ? READ_FAILED_CAPTION
    : rollup
      ? undefined
      : NOT_READ_CAPTION;

  const activeNote = (() => {
    if (!rollup) return undefined;
    if (rollup.coveragePartial) {
      return `Server total; this page read ${rollup.positionCount}`;
    }
    if (rollup.activeCount > 0) return 'On the Loan Participations network';
    return 'No active participations';
  })();

  /**
   * The money tiles cannot be answered from a partial page. Summing page 1 of a
   * 150-position book yields a real number that is not the book's, and it is
   * indistinguishable on screen from a complete one -- so the value is withheld
   * and the caption states the coverage, mirroring the yieldCaption pattern
   * directly below.
   */
  const coverageCaption = (r: PositionRollup): string =>
    `Read ${r.positionCount} of ${r.totalItems ?? r.positionCount}; open Positions for the full book`;

  const moneyValue = (value: number | null): string =>
    rollup && rollup.coveragePartial ? '--' : formatCurrencyCompact(value);

  // `complete` describes a book ('Across all positions', 'Cumulative'). With no
  // rollup there is no book to describe, and the value beside it is '--', so
  // the tile carries withheldCaption alone.
  const moneyNote = (complete: string): string | undefined => {
    if (!rollup) return undefined;
    if (rollup.coveragePartial) return coverageCaption(rollup);
    return complete;
  };

  // The note carries the COVERAGE the value cannot: `--` on its own does not
  // distinguish "no book" from "a book nobody reports a yield for".
  const yieldNote = (() => {
    // 'No active participations' is a MEASURED fact about a book that was read
    // and came back empty. Reaching it through `!rollup` published that fact
    // for a book nobody read -- the GH #2742 shape in caption form.
    if (!rollup) return undefined;
    if (rollup.coveragePartial) return coverageCaption(rollup);
    if (rollup.positionCount === 0) return 'No active participations';
    if (rollup.yieldReportingCount === 0)
      return `No position reports a yield (0 of ${rollup.positionCount})`;
    if (rollup.yieldReportingCount < rollup.positionCount)
      return `Annualized, by position size (${rollup.yieldReportingCount} of ${rollup.positionCount} report)`;
    return 'Annualized, by position size';
  })();

  return (
    <div className="mb-8">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
        <Heading level={2} size="section">
          Loan Participations Performance
        </Heading>
        <Button
          variant="secondary"
          onClick={() => {
            void navigate('/syndication/positions');
          }}
        >
          View Positions
        </Button>
      </div>
      {/*
        The tile half of the unreadable-read contract, mirroring the nav notice
        PortalLayout renders for the same class (c0027162bd). Four tiles reading
        `--` with nothing said is a quieter version of the GH #2742 defect: the
        operator sees withheld figures and no reason to distinguish them from an
        empty book. The notice is what makes the absence attributable.

        `error`, not the nav notice's `warning`: there the nav still functions
        and every surface stays reachable, so the operator can work. Here the
        tile has no answer to give at all.
      */}
      {/*
        GH #1939. NO_PARTICIPANT_FOR_TENANT is the service's ordinary
        not-yet-onboarded answer (loan-syndication-protocol auth.ts), and the
        master ruled on 2026-10-22 that it stands for an impersonation session
        too (ADR-010 down-scope). It is a state of the institution's network
        record, so the notice reads as one: a warning with a support path in
        place of the read-failed notice, and no Retry that would draw the same
        refusal. The four tiles still print `--`, never zeros, and keep the
        kit's refused-read caption (READ_FAILED_CAPTION, "Could not be read"),
        which stays true: the positions read was refused.
      */}
      {notOnboarded && (
        <div className="mb-4" role="alert">
          <Alert type="warning" title={NOT_ONBOARDED_HEADLINE}>
            <div className="space-y-2">
              <div className="text-xs">{failureDetail}</div>
              <div className="text-xs">
                The four figures below are withheld, not zero: there is no
                book to read until onboarding completes.
              </div>
              <Link
                to={requestChangeHref({
                  surface: 'Dashboard -- Loan Participations Performance',
                  item: 'Loan Participations network onboarding',
                  detail: 'The positions read was refused with NO_PARTICIPANT_FOR_TENANT.',
                })}
                className="text-xs text-accent hover:underline"
              >
                Open a support case
              </Link>
            </div>
          </Alert>
        </div>
      )}
      {readFailed && !notOnboarded && (
        <div className="mb-4" role="alert">
          <Alert type="error" title={READ_FAILED_HEADLINE}>
            <div className="space-y-2">
              <div className="text-xs">{failureDetail}</div>
              <div className="text-xs">
                The four figures below are withheld, not zero. Nothing here
                states what your institution holds -- the read itself failed.
              </div>
              <Button
                variant="tertiary"
                size="small"
                onClick={() => {
                  void query.refetch();
                }}
              >
                Retry
              </Button>
            </div>
          </Alert>
        </div>
      )}
      {query.isLoading ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <MetricCard
            label="Active Positions"
            value={activeValue}
            caption={withheldCaption}
            change={activeNote}
            changeType="neutral"
          />
          <MetricCard
            label="CRT Committed"
            value={moneyValue(rollup ? rollup.totalCommitted : null)}
            caption={withheldCaption}
            change={moneyNote('Across all positions')}
            changeType="neutral"
          />
          <MetricCard
            label="Interest Earned"
            value={moneyValue(rollup ? rollup.totalInterest : null)}
            caption={withheldCaption}
            change={moneyNote('Cumulative')}
            changeType={
              rollup && !rollup.coveragePartial && rollup.totalInterest > 0
                ? 'positive'
                : 'neutral'
            }
          />
          <MetricCard
            label="Weighted Yield"
            value={
              rollup && rollup.coveragePartial
                ? '--'
                : formatPercentDash(rollup ? rollup.weightedYield : null)
            }
            caption={withheldCaption}
            change={yieldNote}
            changeType="neutral"
          />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Your Entitlements card (Sprint C15-C WP F-2, 2026-05-03)
// ---------------------------------------------------------------------------
//
// Surfaces the four tenant-scoped entitlement flags so FI operators see at a
// glance which Aaim platform services their institution has contracted. The
// card is read-only -- entitlements are managed by Aaim staff via the
// platform-admin SPA. The footer line is conditional on at least one flag
// being OFF; when all four are enabled, the footer is omitted.
//
// Data source: useInstitutionFlags() hook backed by
// GET /api/admin/tenants/me/feature-flags.
//
// The card renders THREE states, not one. A failed read used to arrive as
// all-false, so an FI that holds PAL and LOS Preview saw four neutral badges
// and a "Request activation" link inviting it to ask for services it already
// pays for -- four fabricated entitlement facts on a contractual surface. The
// read result, the loading window, and the failure are now distinct.

// One label per entitlement, in the nav's vocabulary (r3-fi-g1-11). The chip
// used to carry a short code over a long caption, and two of four captions
// repeated their own chip while 'LOS Preview' disagreed with the nav group
// 'Lending (Preview)'. The second line now states the entitlement's status in
// words, so the badge color is never the only carrier of it.
interface EntitlementBadgeSpec {
  flagKey: keyof InstitutionFlags;
  label: string;
}

const ENTITLEMENT_BADGES: EntitlementBadgeSpec[] = [
  { flagKey: 'palEnabled', label: 'Pledged-Asset Lending' },
  { flagKey: 'enableLosPreview', label: 'Lending (Preview)' },
  { flagKey: 'enablePeValuation', label: 'PE Valuation' },
  { flagKey: 'enableLoanParticipations', label: 'Loan Participations' },
];

function YourEntitlementsCard({
  flags,
  isLoading,
  isError,
  errorDetail,
}: {
  /** null means NOT READ -- never an all-false stand-in. */
  flags: InstitutionFlags | null;
  isLoading: boolean;
  isError: boolean;
  errorDetail: string | null;
}) {
  const allEnabled =
    flags !== null && ENTITLEMENT_BADGES.every((spec) => flags[spec.flagKey]);

  return (
    <Card className="mb-6 p-6">
      <div className="flex items-center justify-between mb-4">
        <Heading level={2} size="card" className="font-sans">
          Your Entitlements
        </Heading>
      </div>
      {isLoading && (
        <div className="flex flex-wrap gap-3 mb-3">
          {ENTITLEMENT_BADGES.map((spec) => (
            <Skeleton key={spec.flagKey} className="h-12 w-32" />
          ))}
        </div>
      )}
      {isError && (
        <div className="bg-error/5 border border-error/20 rounded-sm p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-error flex-shrink-0" />
          <div>
            <div className="text-sm font-sans font-medium text-error">
              {ENTITLEMENTS_READ_FAILED_HEADLINE}
            </div>
            <div className="text-xs font-sans text-error/80 mt-0.5">
              {errorDetail ??
                'The request did not complete. This is not a statement about what your institution holds.'}
            </div>
          </div>
        </div>
      )}
      {flags !== null && (
      <>
      <div className="flex flex-wrap gap-3 mb-3">
        {ENTITLEMENT_BADGES.map((spec) => {
          const isEnabled = flags[spec.flagKey];
          return (
            <div key={spec.flagKey} className="flex flex-col gap-1">
              <Badge variant={isEnabled ? 'success' : 'neutral'} size="medium">
                {spec.label}
              </Badge>
              <BodyText
                variant="ui"
                className={`text-xs ${isEnabled ? 'text-tertiary' : 'text-quaternary'}`}
              >
                {isEnabled ? 'Enabled' : 'Not enabled'}
              </BodyText>
            </div>
          );
        })}
      </div>
      {!allEnabled && (
        // GH #1962: was "Contact your Aaim representative to enable additional
        // services." -- a sentence, not an action. It now files a tracked case.
        <Link
          to={requestChangeHref({
            surface: 'Dashboard -- entitlements',
            item: 'Activate additional services',
            detail: 'Requested from the entitlements card',
          })}
          className="text-xs font-sans text-accent hover:underline mt-2 inline-block"
        >
          Request activation
        </Link>
      )}
      </>
      )}
    </Card>
  );
}

/** What the dashboard says when its summary read fails and the body carries no sentence. */
const DASHBOARD_READ_FAILED_COPY =
  'The dashboard summary could not be read. Retry, or contact support if it persists.';

/**
 * Dashboard - Portfolio Overview
 *
 * Displays the institution's KPI strip (ACTIVE loans and the compliance
 * summary), its entitlements, the Loan Participations tile when entitled, and
 * links to the operational dashboards.
 *
 * The Integration Status section is removed with no successor: no service
 * measures a connection status per FI integration (ADR-038, operator rulings
 * fi-dashboard-integration-status (a) and
 * dashboard-integration-schemas-deletion (a)).
 *
 * Follows institutional design system canonical pattern
 */
export function DashboardPage() {
  const navigate = useNavigate();
  const { data, isLoading, error } = useDashboard();
  const {
    flagsRead: institutionFlags,
    flagsLoading: flagsAreLoading,
    flagsUnreadable: flagsFailed,
    flagsError,
  } = useInstitutionFlags();

  return (
    <>
      {/* Page Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          {/* Page title lives in the sticky topnav (AppHeader); the body opens
              with the descriptive subtitle, no redundant H1. */}
          <p className="text-sm font-sans text-tertiary">Portfolio overview and operational status</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {/*
            This navigates to the compliance reports page; it generates
            nothing. It read "Generate Report" as a red primary CTA, which
            promised an action the click does not perform.
          */}
          <Button
            variant="secondary"
            onClick={() => {
              void navigate('/compliance');
            }}
          >
            <FileText className="w-4 h-4 mr-2" />
            Compliance reports
          </Button>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-error/5 border border-error/20 rounded-sm p-4 mb-6 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-error flex-shrink-0" />
          <div>
            <div className="text-sm font-sans font-medium text-error">Failed to load dashboard</div>
            {/* The legacy client's ApiError message is the transport line
                ("API Error: 500 "); readErrorCopy prints the server's sentence
                or this page's own (fi-g8-14). */}
            <div className="text-xs font-sans text-error/80 mt-0.5">
              {readErrorCopy(error, DASHBOARD_READ_FAILED_COPY)}
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      {isLoading ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          {/*
            DashboardKpi.positive is present only when the note states a
            direction (true favorable, false unfavorable). An absent positive
            is a note stating an absence or a level ('No records', 'In
            portfolio') and draws neutral; mapping it to 'negative' drew it red,
            and the server's required positive: true drew it green. The note
            stays in MetricCard's monospace change slot (GH #2917).
          */}
          {data?.kpis.map((metric, i) => (
            <MetricCard
              key={i}
              label={metric.label}
              value={metric.value}
              change={metric.change}
              changeType={
                metric.positive === true ? 'positive' : metric.positive === false ? 'negative' : 'neutral'
              }
            />
          ))}
        </div>
      )}

      {/* Sprint C15-C WP F-2: Your Entitlements card.
          Read-only summary of the four tenant-scoped entitlement flags
          (PAL, LOS Preview, PE Valuation, Loan Participations). Surfaced
          alongside the KPI strip so FI operators see at a glance which
          Aaim platform services their institution has contracted. */}
      <YourEntitlementsCard
        flags={institutionFlags}
        isLoading={flagsAreLoading}
        isError={flagsFailed}
        errorDetail={
          flagsError instanceof Error ? flagsError.message : null
        }
      />

      {/* Sprint C14 Lane B WP B-7: Loan Participations Performance tile.
          Conditional on enableLoanParticipations. Tenants without the
          flag never see this section -- the nav group is also gated
          via the same flag in PortalLayout.buildFiTenantGroups. */}
      {/*
        Gated on a READ entitlement. `flagsRead` is null while loading and on a
        failed read, so neither state renders the tile -- and neither is treated
        as "this FI is not on the network", which is what an all-false
        placeholder asserted. The card above carries the notice; hiding a tile
        silently would leave the operator with no way to tell the two apart.
      */}
      {institutionFlags?.enableLoanParticipations === true && (
        <LiquidityNetworkPerformanceTile />
      )}

      {/* Operational Dashboards */}
      <div className="mb-8">
        <Heading level={2} size="section">Operational Dashboards</Heading>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
          <Card
            className="cursor-pointer transition-all hover:shadow-md hover:-translate-y-0.5 border border-transparent hover:border-accent/20"
            onClick={() => {
              void navigate('/dashboards/loan-pipeline');
            }}
          >
            <SectionLabel size="sm">Loan Pipeline</SectionLabel>
            <p className="text-sm font-sans text-secondary leading-loose mt-2">
              Application flow, approval rates, origination volume
            </p>
          </Card>
          <Card
            className="cursor-pointer transition-all hover:shadow-md hover:-translate-y-0.5 border border-transparent hover:border-accent/20"
            onClick={() => {
              // The Health Overview nav target; the old alias path now redirects.
              void navigate('/monitoring');
            }}
          >
            <SectionLabel size="sm">Collateral Monitoring</SectionLabel>
            <p className="text-sm font-sans text-secondary leading-loose mt-2">
              Real-time valuations, LTV ratios, collateral alerts
            </p>
          </Card>
          <Card
            className="cursor-pointer transition-all hover:shadow-md hover:-translate-y-0.5 border border-transparent hover:border-accent/20"
            onClick={() => {
              void navigate('/dashboards/compliance-status');
            }}
          >
            <SectionLabel size="sm">Compliance Status</SectionLabel>
            <p className="text-sm font-sans text-secondary leading-loose mt-2">
              Regulatory status, audit trail, EDD reports
            </p>
          </Card>
          <Card
            className="cursor-pointer transition-all hover:shadow-md hover:-translate-y-0.5 border border-transparent hover:border-accent/20"
            onClick={() => {
              void navigate('/dashboards/risk');
            }}
          >
            <SectionLabel size="sm">Risk Dashboard</SectionLabel>
            <p className="text-sm font-sans text-secondary leading-loose mt-2">
              VaR analysis, stress testing, ML-based predictions
            </p>
          </Card>
        </div>
      </div>

      {/* Empty State */}
      {!isLoading && !error && !data?.kpis.length && (
        <div className="text-center py-12">
          <div className="text-sm font-sans text-tertiary">No dashboard data available</div>
        </div>
      )}
    </>
  );
}
